import { create } from 'zustand';
import type { Wallet } from '@asia/shared';
import { mockWallets, mockCoins } from '../lib/mock';
import {
  createIndependentWallet,
  clearWalletSeed,
  renameWalletApi,
  deleteWalletApi,
  type RemoteWallet,
} from '../lib/wallet';

const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const randomAddress = () =>
  'T' + Array.from({ length: 33 }, () => B58[Math.floor(Math.random() * B58.length)]).join('');

// Server wallets only carry address/metadata. Balances stay 0 here — always
// read spendable amounts via useBalances / useWalletBalances (on-chain).
const zeroCoins = () => mockCoins.map((c) => ({ ...c, balance: 0 }));

export const serverToApp = (w: RemoteWallet): Wallet => ({
  id: w.id,
  name: w.name,
  address: w.address,
  coins: zeroCoins(),
  createdAt: w.createdAt,
  derivationIndex: w.derivationIndex,
  seedKind: w.seedKind ?? 'account',
  seedRef: w.seedRef ?? null,
});

interface WalletsState {
  wallets: Wallet[];
  loaded: boolean;
  /** The wallet the user is currently looking at. Every screen that moves money
   *  — sending, buying, topping up a card — acts on this one, so switching
   *  wallets on the main screen changes where the next action lands. */
  activeId: string | null;
  setActive: (id: string) => void;
  /** Session token; set by the wallet gate so actions can hit the API. */
  token: string | null;
  /** Replace the list from the server (or the mock in dev). */
  hydrate: (token: string | null, wallets: Wallet[]) => void;
  /** Create a wallet with its own fresh seed, or import `mnemonic` as one. */
  add: (name?: string, mnemonic?: string) => Promise<Wallet>;
  rename: (id: string, name: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

/** Wallet list — server-backed in real Telegram (addresses derived from the
 *  single seed), falls back to the mock in dev/browser. The selector API
 *  (`useWallets((s) => s.wallets)`, `add/rename/remove`) is unchanged so the
 *  screens don't need to know where the data comes from. */
export const useWallets = create<WalletsState>((set, get) => ({
  wallets: mockWallets,
  loaded: false,
  activeId: mockWallets[0]?.id ?? null,
  token: null,
  setActive: (activeId) => set({ activeId }),
  hydrate: (token, wallets) =>
    set((s) => ({
      token,
      wallets,
      loaded: true,
      // Keep the current choice across a refresh; fall back to the first wallet
      // when it's gone (or when there wasn't one yet).
      activeId: wallets.some((w) => w.id === s.activeId) ? s.activeId : wallets[0]?.id ?? null,
    })),

  add: async (name, mnemonic) => {
    const { token, wallets } = get();
    const label = name ?? `Wallet ${wallets.length + 1}`;
    if (token) {
      const created = serverToApp(await createIndependentWallet(label, { mnemonic }));
      set((s) => ({ wallets: [...s.wallets, created], activeId: s.activeId ?? created.id }));
      return created;
    }
    // Dev fallback (no session) — mock keypair.
    const wallet: Wallet = {
      id: `w${Date.now()}`,
      name: label,
      address: randomAddress(),
      coins: zeroCoins(),
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ wallets: [...s.wallets, wallet], activeId: s.activeId ?? wallet.id }));
    return wallet;
  },

  rename: async (id, name) => {
    const { token } = get();
    if (token) await renameWalletApi(token, id, name).catch(() => {});
    set((s) => ({ wallets: s.wallets.map((w) => (w.id === id ? { ...w, name } : w)) }));
  },

  remove: async (id) => {
    const { token, wallets: current } = get();
    const target = current.find((w) => w.id === id);
    if (token) {
      // The seed ciphertext is removed only once the server confirmed the wallet
      // is gone — never strand a still-registered wallet without its seed.
      const deleted = await deleteWalletApi(token, id).then(
        () => true,
        () => false,
      );
      if (deleted && target) await clearWalletSeed(target);
    }
    set((s) => {
      const wallets = s.wallets.filter((w) => w.id !== id);
      return {
        wallets,
        activeId: s.activeId === id ? wallets[0]?.id ?? null : s.activeId,
      };
    });
  },
}));

/** The wallet every money-moving screen should act on. Falls back to the first
 *  one so a screen never has to handle "nothing selected" separately. */
export function useActiveWallet(): Wallet | undefined {
  return useWallets((s) => s.wallets.find((w) => w.id === s.activeId) ?? s.wallets[0]);
}
